import {
  convertSessionSeparator,
  formatSessionTime,
} from './formatSessionTime';

interface ParsedSchedule {
  timezone: string;
  weekSchedule: string[];
  holidays: string[];
}

export const parseSchedule = (schedule?: string): ParsedSchedule => {
  if (!schedule) {
    return {
      timezone: 'Etc/UTC',
      weekSchedule: Array(7).fill('O'),
      holidays: [],
    };
  }

  const [timezone = 'Etc/UTC', weekScheduleStr = '', holidaysStr = ''] =
    schedule.split(';');

  // Parse week schedule
  const weekSchedule = weekScheduleStr.split(',').map((schedule) => {
    return schedule;
  });

  // Parse holidays
  const holidays = holidaysStr
    .split(',')
    .filter(Boolean) // Remove empty strings
    .map((holiday) => holiday.trim());

  return {
    timezone,
    weekSchedule,
    holidays,
  };
};

/**
 * Converts weekSchedule array to TradingView session format
 * Example: ['0930-1600', '0930-1600', '0930-1600', '0930-1600', '0930-1600', 'C', 'C']
 * becomes: '0930-1600:2|0930-1600:3|0930-1600:4|0930-1600:5|0930-1600:6'
 */
interface SessionGroup {
  time: string;
  days: number[];
}

const optimizeSessionFormat = (
  sessions: { time: string; day: number }[],
): string => {
  // Group by time format
  const groups: SessionGroup[] = [];

  sessions.sort((a, b) => a.day - b.day);

  sessions.forEach(({ time, day }) => {
    const existingGroup = groups.find((g) => g.time === time);
    if (existingGroup) {
      existingGroup.days.push(day);
    } else {
      groups.push({ time, days: [day] });
    }
  });

  // Convert groups to session strings
  return groups.map(({ time, days }) => `${time}:${days.join('')}`).join('|');
};

export const weekScheduleToTradingViewSession = ({
  weekSchedule,
  symbolName,
}: {
  weekSchedule: string[];
  symbolName?: string;
}): string => {
  // Handle special case: all 'O' means 24x7
  if (weekSchedule.every((schedule) => schedule === 'O')) {
    return '24x7';
  }

  const sessions: { time: string; day: number }[] = [];
  weekSchedule.forEach((schedule, index) => {
    // Convert to TradingView day format: Sunday=1, Monday=2, ... Saturday=7.
    // Our `weekSchedule` is Monday-first (index 0 = Monday ... index 6 = Sunday).
    // Sunday (index 6) maps to TradingView day 1, others map to index + 2
    const dayIndex = ((index + 1) % 7) + 1;
    if (schedule === 'O') {
      // For 'O', convert to '0000-0000' format
      sessions.push({ time: '0000-0000', day: dayIndex });
    } else if (schedule !== 'C') {
      // For regular time ranges, use formatSessionTime
      sessions.push({
        time: formatSessionTime({ session: schedule, symbolName }),
        day: dayIndex,
      });
    }
    // Skip 'C' days entirely
  });

  return sessions.length > 0 ? optimizeSessionFormat(sessions) : '24x7';
};

/**
 * Converts holidays array to TradingView corrections format
 * Example: ["0418/C", "0526/C", "0619/C", "0703/0930-1300", "0704/C"]
 * becomes: "0930-1300:20230703"
 */
export interface HolidayFormatResult {
  corrections: string;
  session_holidays: string;
}

export const holidaysToTradingViewInfo = (
  holidays: string[],
  year = new Date().getFullYear(),
): HolidayFormatResult => {
  const corrections: string[] = [];
  const sessionHolidays: string[] = [];

  holidays.forEach((holiday) => {
    const [date, schedule] = holiday.split('/');
    const month = date.substring(0, 2);
    const day = date.substring(2, 4);
    const fullDate = `${year}${month}${day}`;

    if (schedule === 'C') {
      // For closed days, add to session_holidays
      sessionHolidays.push(fullDate);
    } else if (schedule) {
      // For special schedules, convert '&' to ',' for TradingView format
      const formattedSchedule = convertSessionSeparator(schedule);
      corrections.push(`${formattedSchedule}:${fullDate}`);
    }
  });

  return {
    corrections: corrections.join(';'),
    session_holidays: sessionHolidays.join(','),
  };
};
